/**
 * What a blast does to one prop (02 §7, ROUND7 §4): destroys it, strikes it to no effect, or does not reach it.
 * `explode` (damage.ts) acts on the verdict. Pure: the prop, its collider and the blast decide.
 */

import { blastOpens, type Collider, PROPS } from './props';
import { WALLS } from './tuningWalls';
import type { PropDef, PropKind } from './types';

/** `heavy`: LAW rockets and cook-offs. `bomb`: a time bomb, which alone opens `bombOnly` props. */
export type BlastPower = 'grenade' | 'heavy' | 'bomb';

/**
 * Props a blast breaches rather than flattens: laid in short pieces by the generator, and taken only within
 * `WALLS.breachReachFactor` of the blast, so a LAW opens a gap and not a whole face.
 */
const BREACHED: ReadonlySet<PropKind> = new Set<PropKind>(['wall']);

export const isBreached = (kind: PropKind): boolean => BREACHED.has(kind);

/** The most any prop can be reached from, as a fraction of the radius: the cheap test before the exact one. */
const WIDEST = 0.7;

/**
 * - `destroy`: it goes.
 * - `wrongFace`: it would have gone, but the blast struck a face other than its weak side.
 * - `wallNeedsMore`: a grenade went off against a wall, which takes a LAW or a bomb.
 * - null: out of reach, or nothing a blast can harm.
 */
export type BlastVerdict = 'destroy' | 'wrongFace' | 'wallNeedsMore' | null;

/** Squared distance from (x, z) to the nearest point of a collider's footprint (0 inside it), less its radius. */
function reachOf(c: Collider, x: number, z: number): { d2: number; extra: number } {
  const nx = c.shape === 'circle' ? c.x : Math.min(c.x + c.hx, Math.max(c.x - c.hx, x));
  const nz = c.shape === 'circle' ? c.z : Math.min(c.z + c.hz, Math.max(c.z - c.hz, z));
  return { d2: (nx - x) * (nx - x) + (nz - z) * (nz - z), extra: c.shape === 'circle' ? c.r : 0 };
}

/** True if (x, z) could be in reach of this collider at all: `explode` skips the prop lookup for most of the map. */
export function mayReach(c: Collider, x: number, z: number, radius: number): boolean {
  const { d2, extra } = reachOf(c, x, z);
  const reach = extra + radius * WIDEST;
  return d2 <= reach * reach;
}

export function blastVerdict(
  prop: PropDef | undefined, c: Collider, x: number, z: number, radius: number, power: BlastPower,
): BlastVerdict {
  const spec = PROPS[prop?.kind ?? 'rock'];
  if (!prop || spec.durability === 'none') return null;
  if (spec.durability === 'bombOnly' && power !== 'bomb') return null;
  const breached = isBreached(prop.kind);
  if (spec.durability === 'hard' && power === 'grenade' && !breached) return null;
  const factor = breached ? WALLS.breachReachFactor : spec.durability === 'hard' ? 0.55 : 0.7;
  const { d2, extra } = reachOf(c, x, z);
  const reach = extra + radius * factor;
  if (d2 > reach * reach) return null;
  // A wall is `hard`: a grenade in reach of it is refused out loud, not silently ignored (ROUND7 §4).
  if (spec.durability === 'hard' && power === 'grenade') return 'wallNeedsMore';
  // A prop with a weak side shrugs off a blast at any other face - and the player is told (refuseBlast).
  return blastOpens(prop, x, z) ? 'destroy' : 'wrongFace';
}
